/**
 * Emoji — renders content emoji as a Twemoji SVG instead of relying on the
 * OS's native emoji font. Fixes the cross-platform consistency problem
 * (Apple's color-rich emojis vs. Windows' boxy ones vs. Android's anime
 * style) — Twemoji looks identical on every device.
 *
 * Only use for *content / illustration* emojis (vocab card faces, course
 * covers, hero pills). Use the <Icon /> wrapper for functional UI icons.
 *
 * CDN: jsdelivr → Twemoji 14.0.2. ~3KB per icon, lazy-loaded by the browser.
 */

interface Props {
  /** The emoji character, e.g. '🌳' or '🎬'. */
  char: string;
  /** Pixel size (square). Default 32. */
  size?: number;
  className?: string;
}

const CDN = 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg';

/** Convert one or more codepoints into Twemoji's hyphenated-hex filename. */
function toCodepoints(char: string): string {
  return Array.from(char)
    .map((c) => c.codePointAt(0))
    .filter((cp): cp is number => cp !== undefined)
    // Twemoji drops the variation selector (FE0F) from filenames except when
    // it's required to disambiguate. Stripping it works for our cases.
    .filter((cp) => cp !== 0xfe0f)
    .map((cp) => cp.toString(16))
    .join('-');
}

export function Emoji({ char, size = 32, className = '' }: Props): JSX.Element {
  const codepoint = toCodepoints(char);
  return (
    <img
      src={`${CDN}/${codepoint}.svg`}
      alt={char}
      width={size}
      height={size}
      loading="lazy"
      decoding="async"
      className={`inline-block align-middle ${className}`}
      style={{ width: size, height: size }}
      // Graceful fallback to native emoji if CDN ever fails.
      onError={(e) => {
        const img = e.currentTarget;
        img.replaceWith(Object.assign(document.createElement('span'), {
          textContent: char,
          style: `display:inline-block;font-size:${size}px;line-height:1;vertical-align:middle`,
        }));
      }}
    />
  );
}
